import { api } from './api';
import { Anime, AnimeDetail, AnimeReviewData } from '../types/Anime';

export interface AnimeSearchFilters {
  genres?: number[];
  type?: string;
  season?: string;
  year?: number | '';
  order?: string;
}

export const searchAnime = (
  query: string,
  page: number,
  filters: AnimeSearchFilters = {}
): Promise<{ results: Anime[]; count: number }> => {
  const params = new URLSearchParams({
    q: query,
    page: page.toString(),
  });

  if (filters.genres && filters.genres.length > 0) {
    params.append('genres', filters.genres.join(','));
  }
  if (filters.type) {
    params.append('type', filters.type);
  }
  if (filters.season) {
    params.append('season', filters.season);
  }
  if (filters.year) {
    params.append('year', filters.year.toString());
  }
  if (filters.order && filters.order !== '') {
    params.append('order', filters.order);
  }

  return api.get(`/films/anime/search/?${params.toString()}`).then(response => {
    return {
      results: response.data.results || [],
      count: response.data.count || 0,
    };
  });
};

export const getAnimeDetails = (malId: number): Promise<AnimeDetail> => {
  return api.get(`/films/anime/${malId}/`).then(response => response.data);
};

export const getAnimeByGenre = (genreId: string, page: number = 1) => {
  return api.get(`/films/anime/by-genre/${genreId}/`, { params: { page } }).then(r => r.data);
};

export const getAnimeByStudio = (studioId: string, page: number = 1) => {
  return api.get(`/films/anime/by-studio/${studioId}/`, { params: { page } }).then(r => r.data);
};

export const getAnimeByProducer = (producerId: string, page: number = 1) => {
  return api.get(`/films/anime/by-producer/${producerId}/`, { params: { page } }).then(r => r.data);
};

// ============================================================
// Anime Status API
// ============================================================

export const setAnimeStatus = (malId: number, status: number | null): Promise<any> => {
  return api.post('/films/user-anime-status/set_status/', { mal_id: malId, status }).then(r => r.data);
};

export interface AnimeStatusResponse {
  status: number | null;
  score: number | null;
  score_tags: number[] | null;
  score_story: number | null;
  score_animation: number | null;
  score_sound: number | null;
  score_character: number | null;
  score_direction: number | null;
  episodes_watched: number | null;
  watch_medium: number[] | null;
  watch_style: number[] | null;
  start_date: string | null;
  end_date: string | null;
  rewatch: boolean;
  review_text: string | null;
}

export const getAnimeStatus = (malId: number): Promise<AnimeStatusResponse> => {
  return api.get('/films/user-anime-status/get_status/', { params: { mal_id: malId } }).then(r => r.data);
};

export const setAnimeReview = (malId: number, data: AnimeReviewData): Promise<any> => {
  return api.post('/films/user-anime-status/set_review/', {
    mal_id: malId,
    score: data.score,
    review_text: data.reviewText,
    score_tags: data.scoreTags.length > 0 ? data.scoreTags : null,
    score_story: data.scoreStory,
    score_animation: data.scoreAnimation,
    score_sound: data.scoreSound,
    score_character: data.scoreCharacter,
    score_direction: data.scoreDirection,
    episodes_watched: data.episodesWatched,
    watch_medium: data.watchMedium.length > 0 ? data.watchMedium : null,
    watch_style: data.watchStyle.length > 0 ? data.watchStyle : null,
    start_date: data.startDate,
    end_date: data.endDate,
    rewatch: data.rewatch,
  }).then(r => r.data);
};

// ============================================================
// Paginated Detail Data
// ============================================================

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const getAnimeCharacters = (malId: number, page: number): Promise<PaginatedResponse<any>> => {
  return api.get(`/films/anime/${malId}/characters/`, { params: { page } }).then(r => r.data);
};

export const getAnimeStaffs = (malId: number, page: number): Promise<PaginatedResponse<any>> => {
  return api.get(`/films/anime/${malId}/staffs/`, { params: { page } }).then(r => r.data);
};

export const getBasicAnimeStatuses = (malIds: number[]): Promise<Record<number, number>> => {
  return api.get('/films/user-anime-status/get_basic_statuses/', {
    params: { mal_ids: malIds.join(',') }
  }).then(r => r.data);
};

// ============================================================
// Library API
// ============================================================

export const getAnimeByStatus = (
  status: number,
  page?: number,
  query?: string,
  order?: string
) => api.get('/films/user-anime-status/by_status/', { params: { status, page, query, order } });

export const getAnimeByStatusForUser = (
  username: string,
  status: number,
  page?: number,
  query?: string,
  order?: string
) => api.get(`/films/user-anime-status/by_status/${username}/`, { params: { status, page, query, order } });

export const getProfileAnimeStatuses = (malIds: number[]): Promise<Record<number, {
  status: number | null;
  score: number | null;
  review_text: string | null;
  start_date: string | null;
  end_date: string | null;
  sub_status: number[] | null;
  score_tags: number[] | null;
  episodes_watched: number | null;
}>> => api.get('/films/user-anime-status/get_profile_statuses/', {
  params: { mal_ids: malIds.join(',') }
}).then(r => r.data);

export const getPublicAnimeStatuses = (username: string, malIds: number[]): Promise<Record<number, {
  status: number | null;
  score: number | null;
  review_text: string | null;
  start_date: string | null;
  end_date: string | null;
  sub_status: number[] | null;
  score_tags: number[] | null;
  episodes_watched: number | null;
}>> => api.get(`/films/user-anime-status/get_public_statuses/${username}/`, {
  params: { mal_ids: malIds.join(',') }
}).then(r => r.data);

// ============================================================
// Anime Reviews API
// ============================================================

export interface AnimeReviewDetail {
  id: number | string;
  mal_id: number;
  anime: {
    mal_id: number;
    title: string;
    title_japanese?: string | null;
    title_english?: string | null;
    image_url?: string | null;
    episodes?: number | null;
    aired_from?: string | null;
  };
  review_text: string;
  created_at: string;
  updated_at: string;
  score: number | null;
  score_tags: number[] | null;
  score_story: number | null;
  score_animation: number | null;
  score_sound: number | null;
  score_character: number | null;
  score_direction: number | null;
  episodes_watched: number | null;
  watch_medium: number[] | null;
  watch_style: number[] | null;
  start_date: string | null;
  end_date: string | null;
  like_count: number;
}

export const getAnimeReviewsByUser = async (
  username: string,
  page: number = 1,
  query: string = '',
  order: string = 'default'
) => {
  const params = new URLSearchParams({ page: page.toString() });
  if (query) params.append('query', query);
  if (order !== 'default') params.append('order', order);
  const response = await api.get(`/films/anime-reviews/user/${username}/?${params.toString()}`);
  return response.data;
};
